/**
 * Реплики Барбы — старого наставника Луция из сюжетной кампании — в начале
 * каждой главы обучения. Черновики автора — developer_artifacts/tutorial/
 * barba_replica_chapter_N.md.
 *
 * Каждое окно — отдельный чисто модальный шаг главы (buildTutorialBarbaSteps
 * ставится в начало chapter.steps): закрытие окна продвигает сценарий,
 * попадает в запись прохождения и в метрики шагов с номером, как любая
 * обучающая модалка. Окна одной главы идут цепочкой, за последним — обычная
 * приветственная модалка главы.
 *
 * Финальные окна (TUTORIAL_BARBA_OUTRO_REPLICAS) — шаги в конце главы, после
 * последнего задания: глава засчитывается закрытием последнего окна, и только
 * тогда появляются поздравление и награда.
 *
 * Только данные, без React: конфиги глав импортирует сервер.
 *
 * Окно: paragraphs — абзацы реплики; buttonText — текст единственной кнопки.
 */

export const TUTORIAL_SPEAKERS = Object.freeze({
  BARBA: 'barba',
});

export const TUTORIAL_BARBA_REPLICAS = {
  ch1: [
    {
      paragraphs: [
        'Сальве, новобранец!',
        'Я Барба, старый наставник Луция. Он теперь легат — чин немалый. Обратился ко мне с просьбой: обучить тебя основам Стратегикона.',
        'А просьба легата — тот же приказ. Только вежливый.',
      ],
      buttonText: 'Слушаю',
    },
    {
      paragraphs: [
        'Что ж, опытом поделюсь. Но предупреждаю: учить буду строго по делу, без соплей и воды. Длинно у нас только врут.',
        'Впереди три сухих урока, по 7 минут каждый. Не вытерпишь даже этого — найди себе другое занятие. Такие достойными командирами не становятся.',
      ],
      buttonText: 'Вытерплю',
    },
    {
      paragraphs: [
        'Думал, я буду с тобой любезничать? Угу, как же.',
        'Моё дело одно — сделать из тебя закалённого командира, который сам отвечает за свои решения.',
        'Хватит слов — приступаем! Про снабжение не забывай: голодный солдат воюет плохо. Мёртвый — ещё хуже, зато не ест.',
      ],
      buttonText: 'К делу!',
    },
  ],
  ch2: [
    {
      paragraphs: [
        'Смотри-ка, не всё с тобой потеряно. А я уж думал — зря время трачу.',
        'Осталось два урока. Потом выпущу тебя в настоящий бой — сразу с двумя соперниками.',
      ],
      buttonText: 'Дальше',
    },
    {
      paragraphs: [
        'Всегда помни: только настоящий бой покажет, чего ты стоишь как полководец.',
        'А пока — снова орки. Упрямый народ: даже умирать не торопятся. Придётся помочь.',
      ],
      buttonText: 'За работу',
    },
  ],
  ch3: [
    {
      paragraphs: [
        'Неплохо идёшь. Даже подозрительно. На сегодня — последний урок.',
        'Только не зазнавайся: зазнавшихся я хоронил чаще, чем трусов.',
      ],
      buttonText: 'Слушаю',
    },
    {
      paragraphs: [
        'Заметь: побеждают не только мечом.',
        'Врага можно задавить монетой. От неё щитом не закроешься.',
        'А можно перехитрить — политикой и ловким словом. Я этому так и не научился: мечом проще, там хоть видно, кого бить.',
      ],
      buttonText: 'Запомню',
    },
    {
      paragraphs: [
        'Закончишь эту главу — обязательно иди в бой. Сразу, пока урок не выветрился.',
        'И не опозорь меня перед соперниками: краснеть в мои годы вредно.',
      ],
      buttonText: 'Приступим',
    },
  ],
};

// Напутствие в конце последней главы: следом — поздравление с кнопкой
// «В битву» (готовая локальная партия против двух ботов, см. configs/newbieBattle)
export const TUTORIAL_BARBA_OUTRO_REPLICAS = {
  ch3: [
    {
      paragraphs: [
        'Ну вот. Три урока позади, и форпост вентори — твой.',
        'Ругать почти не за что. А я, поверь, искал.',
        'Громче не похвалю: громко хвалят только над погребальным костром. Тебе туда рано.',
      ],
      buttonText: 'И не спешу',
    },
    {
      paragraphs: [
        'Теперь главное. Следом будет кнопка «В битву». Жми её сразу — бой уже собран: ты за орков, против тебя вентори и гномы.',
        'Проиграть не страшно: ни ставок, ни чужих глаз — только ты и наука. Страшно отложить: урок до утра не доживёт, как раненый без лекаря.',
      ],
      buttonText: 'Не отложу',
    },
  ],
};

// Часть главы, где говорит Барба: вступление или финал
export const TUTORIAL_BARBA_PARTS = Object.freeze({
  INTRO: 'intro',
  OUTRO: 'outro',
});

const REPLICAS_BY_PART = {
  [TUTORIAL_BARBA_PARTS.INTRO]: TUTORIAL_BARBA_REPLICAS,
  [TUTORIAL_BARBA_PARTS.OUTRO]: TUTORIAL_BARBA_OUTRO_REPLICAS,
};

// Префикс id шага и модалки: у финальных окон свой, чтобы не совпасть с
// окнами вступления той же главы
const PART_PREFIXES = {
  [TUTORIAL_BARBA_PARTS.INTRO]: 'barba',
  [TUTORIAL_BARBA_PARTS.OUTRO]: 'barba_outro',
};

export const getTutorialBarbaModalId = (chapterKey, windowIndex, part = TUTORIAL_BARBA_PARTS.INTRO) => (
  `${chapterKey}_${PART_PREFIXES[part]}_${windowIndex + 1}`
);

/**
 * Шаги главы с окнами Барбы: по шагу на окно, у каждого свой modalId — номер
 * шага в метриках и текст окна берутся по нему
 *
 * @param {string} chapterKey - ключ главы (ch1, ch2, ch3)
 * @param {string} [part] - TUTORIAL_BARBA_PARTS: вступление (в начало steps)
 *   или финал (в конец steps)
 * @returns {Object[]}
 */
export const buildTutorialBarbaSteps = (chapterKey, part = TUTORIAL_BARBA_PARTS.INTRO) => (
  (REPLICAS_BY_PART[part]?.[chapterKey] || []).map((replica, windowIndex) => ({
    id: `${PART_PREFIXES[part]}_${windowIndex + 1}`,
    modalId: getTutorialBarbaModalId(chapterKey, windowIndex, part),
    speaker: TUTORIAL_SPEAKERS.BARBA,
  }))
);

const BARBA_REPLICAS_BY_MODAL_ID = Object.fromEntries(
  Object.entries(REPLICAS_BY_PART).flatMap(([part, replicasByChapter]) => (
    Object.entries(replicasByChapter).flatMap(([chapterKey, replicas]) => replicas.map(
      (replica, windowIndex) => [getTutorialBarbaModalId(chapterKey, windowIndex, part), replica]
    ))
  ))
);

export const TUTORIAL_BARBA_MODAL_IDS = Object.freeze(Object.keys(BARBA_REPLICAS_BY_MODAL_ID));

export const getTutorialBarbaReplica = (modalId) => BARBA_REPLICAS_BY_MODAL_ID[modalId] || null;
